import { ReactNode } from 'react';
import CurrencyField from './CurrencyInput';
import DateField from './Date';
import DropdownField from './DropdownField';
import InputField from './InputField';
import LabelNoHover from './LabelNoHover';
import MultiSelectField from './MultiSelectField';
import NumberField from './NumberField';
import PercentageField from './PercentageField';
import Summation from './Summation';
import ChildRowList from './ChildRowList';
import TableSection from './TableSection';
import TextAreaField from './TextAreaField';
import { fieldTypes } from '../constants/app';
import { ILabel, ILabelOption } from '../constants/interfaces';
import { staticTextStyles } from '../styles/fieldStyles';

/** When true, `summation` fields use ChildRowList instead of Summation (testing only). */
const USE_CHILD_ROW_LIST_FOR_SUMMATION = true;

/** Fallback for `yesOrNo` fields that have no options configured in the database. */
export const yesNoOptions: ILabelOption[] = [
  {
    isDefault: false,
    labelOptionId: 1,
    parentLabelId: 0,
    orderIndex: 0,
    text: 'Yes',
  },
  {
    isDefault: false,
    labelOptionId: 0,
    parentLabelId: 0,
    orderIndex: 1,
    text: 'No',
  },
];

/** Everything a renderer may need. `commonProps` covers the usual field contract. */
export interface IFieldRenderContext {
  commonProps: Record<string, unknown>;
  field: ILabel;
  formStateForVisibility?: Record<string, unknown>;
  isInGrid?: boolean;
  onAuxiliaryFieldChange: (key: number, text: string) => void;
  onChange: (value: unknown, children?: unknown, labelId?: number) => void;
  resetTrigger?: number;
}

export interface IFieldRenderer {
  render: (ctx: IFieldRenderContext) => ReactNode;
  /** Fills the grid cell rather than sitting at the default input width. */
  fullWidthInGrid?: boolean;
  /** Stretches to fill its container in both axes (nested, self-laying-out fields). */
  stretch?: boolean;
  /** Owns child labels, linked by parent_label_id in their visibility rules. */
  isComposite?: boolean;
  /** Its children are line items that may carry nested labels of their own. */
  hasLineItems?: boolean;
}

/**
 * The field components each declare their own prop types, so they are widened to a
 * common shape here. The cast is on the component rather than on commonProps, which
 * stays a real object and can therefore be spread.
 */
type FieldComponent = React.ComponentType<Record<string, unknown>>;

const asField = (Component: unknown) => Component as FieldComponent;

const ChildRowListField = asField(ChildRowList);
const SummationField = asField(Summation);
const DropdownFieldAny = asField(DropdownField);

/** Most types just take the common field contract. */
const standard =
  (Component: unknown, extra: Record<string, unknown> = {}) =>
  ({ commonProps }: IFieldRenderContext) => {
    const Any = asField(Component);
    return <Any {...commonProps} {...extra} />;
  };

/** Non-interactive types that only present their configured text. */
const staticText =
  (style?: React.CSSProperties, withRequired = false) =>
  ({ field }: IFieldRenderContext) => (
    <LabelNoHover
      text={field.text}
      required={withRequired ? field.isMandatory : undefined}
      style={style}
      noBorder={!withRequired}
    />
  );

/**
 * How each configured label type is rendered.
 *
 * Keyed by the `type` on the label row, so a type is wired up by adding an entry
 * here rather than by extending a switch in the middle of a component. The width
 * hints live alongside the renderer for the same reason — they were previously
 * two separate hardcoded lists of type names that had to be kept in step by hand.
 */
export const fieldRenderers: Record<string, IFieldRenderer> = {
  [fieldTypes.break]: {
    render: () => <div style={{ width: '100%' }} />,
    fullWidthInGrid: true,
  },
  [fieldTypes.childRowList]: {
    isComposite: true,
    hasLineItems: true,
    render: ({
      commonProps,
      formStateForVisibility,
      isInGrid,
      onAuxiliaryFieldChange,
      resetTrigger,
    }) => (
      <ChildRowListField
        {...commonProps}
        formStateForVisibility={formStateForVisibility}
        onAuxiliaryFieldChange={onAuxiliaryFieldChange}
        resetTrigger={resetTrigger}
        isInGrid={isInGrid}
      />
    ),
    fullWidthInGrid: true,
    stretch: true,
  },
  [fieldTypes.currency]: { render: standard(CurrencyField) },
  [fieldTypes.date]: { render: standard(DateField) },
  [fieldTypes.decimal]: { render: standard(NumberField) },
  [fieldTypes.dropdown]: { render: standard(DropdownField) },
  [fieldTypes.float]: { render: standard(NumberField) },
  [fieldTypes.information]: {
    render: staticText(staticTextStyles.information),
    fullWidthInGrid: true,
  },
  [fieldTypes.integer]: { render: standard(NumberField) },
  [fieldTypes.label]: {
    render: staticText(undefined, true),
    fullWidthInGrid: true,
  },
  [fieldTypes.multiple]: {
    render: standard(MultiSelectField),
    fullWidthInGrid: true,
  },
  [fieldTypes.percentage]: { render: standard(PercentageField) },
  [fieldTypes.sum]: { render: standard(CurrencyField, { displayOnly: true }) },
  [fieldTypes.summation]: {
    isComposite: true,
    hasLineItems: true,
    render: (ctx) => {
      const { commonProps, isInGrid, onAuxiliaryFieldChange, resetTrigger } =
        ctx;
      if (USE_CHILD_ROW_LIST_FOR_SUMMATION) {
        return (
          <ChildRowListField
            {...commonProps}
            onAuxiliaryFieldChange={onAuxiliaryFieldChange}
            resetTrigger={resetTrigger}
            isInGrid={isInGrid}
          />
        );
      }
      return (
        <SummationField
          {...commonProps}
          resetTrigger={resetTrigger}
          isInGrid={isInGrid}
        />
      );
    },
    fullWidthInGrid: true,
    stretch: true,
  },
  [fieldTypes.table]: {
    isComposite: true,
    render: ({ field, onChange, resetTrigger }) => (
      <TableSection
        field={field}
        onValueChange={onChange}
        resetTrigger={resetTrigger}
      />
    ),
  },
  [fieldTypes.text]: { render: standard(InputField) },
  [fieldTypes.textArea]: { render: standard(TextAreaField) },
  [fieldTypes.warning]: {
    render: staticText(staticTextStyles.warning),
    fullWidthInGrid: true,
  },
  [fieldTypes.yesOrNo]: {
    // Configured options win; the Yes/No pair is only a fallback for label rows
    // that have none.
    render: ({ commonProps, field }) => (
      <DropdownFieldAny
        {...commonProps}
        field={{
          ...field,
          options: field.options?.length ? field.options : yesNoOptions,
        }}
      />
    ),
  },
};

/**
 * The same label rows are rendered by two forms, and the response form has always
 * accepted synonyms for several types ('string' for text, 'select' for dropdown and
 * so on). Rather than each form knowing its own spellings, every alias resolves to
 * the canonical fieldTypes value here.
 */
const fieldTypeAliases: Record<string, string> = {
  string: fieldTypes.text,
  multiline: fieldTypes.textArea,
  select: fieldTypes.dropdown,
  datetime: fieldTypes.date,
  number: fieldTypes.integer,
  int: fieldTypes.integer,
  yes_no: fieldTypes.yesOrNo,
  yesno: fieldTypes.yesOrNo,
};

/** Canonical type for a configured label, case- and synonym-insensitive. */
export const resolveFieldType = (type?: string): string | undefined => {
  if (!type) return undefined;
  const normalised = type.trim().toLowerCase();
  return fieldTypeAliases[normalised] ?? normalised;
};

/** Owns child labels (table, summation, child row list). */
export const isCompositeFieldType = (type?: string): boolean =>
  Boolean(getFieldRenderer(type)?.isComposite);

/** Children are line items that may carry nested labels of their own. */
export const hasLineItemChildren = (type?: string): boolean =>
  Boolean(getFieldRenderer(type)?.hasLineItems);

export const getFieldRenderer = (type?: string): IFieldRenderer | undefined => {
  const canonical = resolveFieldType(type);
  return canonical ? fieldRenderers[canonical] : undefined;
};
